import React, { useContext, useEffect, useRef, useState } from "react";
import { CanvasContext } from "../../../contexts/CanvasContext";
import LOGOIMAGE from "../../../asset/nexGenBlue.gif";

export default function ImageComponent({ image }) {
  const imageRef = useRef(null);
  const { setCurrentPage, pages, hoveredDivision, files } =
    useContext(CanvasContext);
  const [imageUrl, setImageUrl] = useState(null);
  const isHovered = image.id === hoveredDivision;
  const [isimageHovered, setIsimageHovered] = useState(false);
  const [isimageFocused, setIsimageFocused] = useState(false);
  const [isimageActive, setIsimageActive] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      if (image.src && image.src.startsWith("./")) {
        const imageName = image.src.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setImageUrl(image.src);
      }
    };

    fetchImage();
  }, [image.src, files]);

  const generateKeyframes = () => {
    if (!image.keyframes || image.keyframes.length === 0) return null;

    return image.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        // Remove 'KEY' prefix if present
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        // Handle transform properties
        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          // Convert camelCase to kebab-case for other CSS properties
          properties[processedKey] = value;
        }
      });

      // Add transform property if it was built
      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };
  useEffect(() => {
    if (imageRef.current && image.keyframes && image.keyframes.length > 0) {
      const keyframes = generateKeyframes();
      const animation = imageRef.current.animate(keyframes, {
        duration: parseFloat(image.animationDuration) * 1000 || 1000,
        delay: parseFloat(image.animationDelay) * 1000 || 0,
        iterations:
          image.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(image.animationIterationCount) || 1,
        direction: image.animationDirection || "normal",
        fill: image.animationFillMode || "none",
        easing: image.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    image.keyframes,
    image.animationDuration,
    image.animationDelay,
    image.animationIterationCount,
    image.animationDirection,
    image.animationFillMode,
    image.animationTimingFunction,
  ]);

  const focusedStyles =
    isimageFocused && image.pseudoClass === "focus"
      ? {
          width: image.focusedwidth || image.width || "200px",
          height: image.focusedheight || image.height || "auto",

          padding: image.focusedpadding || image.padding || "0",
          paddingTop: image.focusedpaddingTop || image.focusedpadding || "0",
          paddingRight:
            image.focusedpaddingRight || image.focusedpadding || "0",
          paddingBottom:
            image.focusedpaddingBottom || image.focusedpadding || "0",
          paddingLeft: image.focusedpaddingLeft || image.focusedpadding || "0",
          margin: image.focusedmargin || image.margin || "0px",
          marginTop: image.focusedmarginTop || image.focusedmargin || "0px",
          marginRight: image.focusedmarginRight || image.focusedmargin || "0px",
          marginBottom:
            image.focusedmarginBottom || image.focusedmargin || "0px",
          marginLeft: image.focusedmarginLeft || image.focusedmargin || "0px",

          borderTop:
            image.focusedborderTop ||
            image.focusedborder ||
            image.border ||
            "none",
          borderRight:
            image.focusedborderRight ||
            image.focusedborder ||
            image.border ||
            "none",
          borderBottom:
            image.focusedborderBottom ||
            image.focusedborder ||
            image.border ||
            "none",
          borderLeft:
            image.focusedborderLeft ||
            image.focusedborder ||
            image.border ||
            "none",
          borderSapcing:
            image.focusedborderSpacing || image.borderSpacing || "0px",
          borderCollapse:
            image.focusedborderCollapse || image.borderCollapse || "0px",
          borderRadius: image.focusedborderRadius || image.borderRadius || "0",
          outline: image.focusedoutline || image.outline || "none",

          position: image.focusedposition || image.position || "relative",
          top: image.focusedtop || image.top || "auto",
          right: image.focusedright || image.right || "auto",
          bottom: image.focusedbottom || image.bottom || "auto",
          left: image.focusedleft || image.left || "auto",
          zIndex: image.focusedzIndex || image.zIndex || "auto",
          boxShadow: image.focusedboxShadow || image.boxShadow || "none",
          opacity: image.focusedOpacity || image.opacity || 1,
          cursor: image.focusedcursor || image.cursor || "default",
          visibility: image.focusedvisibility || "visible",

          filter: image.focusedfilter || image.filter || "none",
          backdropFilter:
            image.focusedbackdropFilter || image.backdropFilter || "none",
          mixBlendMode:
            image.focusedmixBlendMode || image.mixBlendMode || "normal",
          transform: image.focusedtransform || image.transform || "none",
          transition: image.focusedtransition || image.transition || "none",
        }
      : {};

  const hoverStyles =
    isimageHovered && image.pseudoClass === "hover"
      ? {
          width: image.hoveredwidth || image.width || "200px",
          height: image.hoveredheight || image.height || "auto",

          padding: image.hoveredpadding || image.padding || "0",
          paddingTop: image.hoveredpaddingTop || image.hoveredpadding || "0",
          paddingRight:
            image.hoveredpaddingRight || image.hoveredpadding || "0",
          paddingBottom:
            image.hoveredpaddingBottom || image.hoveredpadding || "0",
          paddingLeft: image.hoveredpaddingLeft || image.hoveredpadding || "0",
          margin: image.hoveredmargin || image.margin || "0px",
          marginTop: image.hoveredmarginTop || image.hoveredmargin || "0px",
          marginRight: image.hoveredmarginRight || image.hoveredmargin || "0px",
          marginBottom:
            image.hoveredmarginBottom || image.hoveredmargin || "0px",
          marginLeft: image.hoveredmarginLeft || image.hoveredmargin || "0px",

          borderTop:
            image.hoveredborderTop ||
            image.hoveredborder ||
            image.border ||
            "none",
          borderRight:
            image.hoveredborderRight ||
            image.hoveredborder ||
            image.border ||
            "none",
          borderBottom:
            image.hoveredborderBottom ||
            image.hoveredborder ||
            image.border ||
            "none",
          borderLeft:
            image.hoveredborderLeft ||
            image.hoveredborder ||
            image.border ||
            "none",
          borderSapcing:
            image.hoveredborderSpacing || image.borderSpacing || "0px",
          borderCollapse:
            image.hoveredborderCollapse || image.borderCollapse || "0px",
          borderRadius: image.hoveredborderRadius || image.borderRadius || "0",
          outline: image.hoveredoutline || image.outline || "none",

          position: image.hoveredposition || image.position || "relative",
          top: image.hoveredtop || image.top || "auto",
          right: image.hoveredright || image.right || "auto",
          bottom: image.hoveredbottom || image.bottom || "auto",
          left: image.hoveredleft || image.left || "auto",
          zIndex: image.hoveredzIndex || image.zIndex || "auto",
          boxShadow: image.hoveredboxShadow || image.boxShadow || "none",
          opacity: image.hoveredOpacity || image.opacity || 1,
          cursor: image.hoveredcursor || image.cursor || "default",
          visibility: image.hoveredvisibility || "visible",

          filter: image.hoveredfilter || image.filter || "none",
          backdropFilter:
            image.hoveredbackdropFilter || image.backdropFilter || "none",
          mixBlendMode:
            image.hoveredmixBlendMode || image.mixBlendMode || "normal",
          transform: image.hoveredtransform || image.transform || "none",
          transition: image.hoveredtransition || image.transition || "none",
        }
      : {};

  const activeStyles =
    isimageActive && image.pseudoClass === "active"
      ? {
          width: image.activatedwidth || image.width || "200px",
          height: image.activatedheight || image.height || "auto",

          padding: image.activatedpadding || image.padding || "0",
          paddingTop:
            image.activatedpaddingTop || image.activatedpadding || "0",
          paddingRight:
            image.activatedpaddingRight || image.activatedpadding || "0",
          paddingBottom:
            image.activatedpaddingBottom || image.activatedpadding || "0",
          paddingLeft:
            image.activatedpaddingLeft || image.activatedpadding || "0",
          margin: image.activatedmargin || image.margin || "0px",
          marginTop: image.activatedmarginTop || image.activatedmargin || "0px",
          marginRight:
            image.activatedmarginRight || image.activatedmargin || "0px",
          marginBottom:
            image.activatedmarginBottom || image.activatedmargin || "0px",
          marginLeft:
            image.activatedmarginLeft || image.activatedmargin || "0px",

          borderTop:
            image.activatedborderTop ||
            image.activatedborder ||
            image.border ||
            "none",
          borderRight:
            image.activatedborderRight ||
            image.activatedborder ||
            image.border ||
            "none",
          borderBottom:
            image.activatedborderBottom ||
            image.activatedborder ||
            image.border ||
            "none",
          borderLeft:
            image.activatedborderLeft ||
            image.activatedborder ||
            image.border ||
            "none",
          borderSapcing:
            image.activatedborderSpacing || image.borderSpacing || "0px",
          borderCollapse:
            image.activatedborderCollapse || image.borderCollapse || "0px",
          borderRadius:
            image.activatedborderRadius || image.borderRadius || "0",
          outline: image.activatedoutline || image.outline || "none",

          position: image.activatedposition || image.position || "relative",
          top: image.activatedtop || image.top || "auto",
          right: image.activatedright || image.right || "auto",
          bottom: image.activatedbottom || image.bottom || "auto",
          left: image.activatedleft || image.left || "auto",
          zIndex: image.activatedzIndex || image.zIndex || "auto",
          
          boxShadow: image.activatedboxShadow || image.boxShadow || "none",
          opacity: image.activatedOpacity || image.opacity || 1,
          cursor: image.activatedcursor || image.cursor || "default",
          visibility: image.activatedvisibility || "visible",

          filter: image.activatedfilter || image.filter || "none",
          backdropFilter:
            image.activatedbackdropFilter || image.backdropFilter || "none",
          mixBlendMode:
            image.activatedmixBlendMode || image.mixBlendMode || "normal",
          transform: image.activatedtransform || image.transform || "none",
          transition: image.activatedtransition || image.transition || "none",
        }
      : {};

  const ImageStyle = {
    width: image.width || "200px",
    height: image.height || "auto",
    paddingTop: image.paddingTop || image.padding || "0px",
    paddingRight: image.paddingRight || image.padding || "0px",
    paddingBottom: image.paddingBottom || image.padding || "0px",
    paddingLeft: image.paddingLeft || image.padding || "0px",
    marginTop: image.marginTop || image.margin || "0px",
    marginRight: image.marginRight || image.margin || "0px",
    marginBottom: image.marginBottom || image.margin || "0px",
    marginLeft: image.marginLeft || image.margin || "0px",

    borderTop: isHovered
      ? "1px dashed #555"
      : image.borderTop || image.border || "none",
    borderRight: isHovered
      ? "1px dashed #555"
      : image.borderRight || image.border || "none",
    borderBottom: isHovered
      ? "1px dashed #555"
      : image.borderBottom || image.border || "none",
    borderLeft: isHovered
      ? "1px dashed #555"
      : image.borderLeft || image.border || "none",
    borderSapcing: image.borderSpacing || "0px",
    borderCollapse: image.borderCollapse || "0px",
    borderRadius: image.borderRadius || "0",
    outline: image.outline || "none",

    position: image.position || "static",
    top: image.top || "auto",
    right: image.right || "auto",
    bottom: image.bottom || "auto",
    left: image.left || "auto",
    zIndex: image.zIndex || "auto",
    
    boxShadow: image.boxShadow || "none",
    opacity: image.opacity || "1",
    boxSizing: image.boxSizing || "content-box",
    display: image.display || "block",
    visibility: image.visibility || "visible",

    
    filter: image.filter || "none",
    backdropFilter: image.backdropFilter || "none",
    mixBlendMode: image.mixBlendMode || "normal",
    objectFit: image.objectFit || "fill",
    objectPosition: image.objectPosition || "center",
    cursor: image.cursor || "auto",
    transition: image.transition || "none",
    transform: image.transform || "none",

    
    scale: isHovered ? "1.01" : "1",
    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  const handleClick = () => {
    if (image.redirectTo) {
      const targetPage = pages.find((page) => page.name === image.redirectTo);
      if (targetPage) {
        setCurrentPage(targetPage);
      } else {
        console.error(`Page "${image.redirectTo}" not found`);
      }
    } else if (image.redirectUrl) {
      window.open(image.redirectUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <img
      ref={imageRef}
      style={ImageStyle}
      src={imageUrl || LOGOIMAGE}
      alt={image.alt || "Image"}
      title={image.title || "NexGen"}
      onClick={handleClick}
      onMouseEnter={() => setIsimageHovered(true)}
      onMouseLeave={() => setIsimageHovered(false)}
      onFocus={() => setIsimageFocused(true)}
      onBlur={() => setIsimageFocused(false)}
      onMouseDown={() => setIsimageActive(true)}
      onMouseUp={() => setIsimageActive(false)}
    />
  );
}
